<!-- eslint-disable vue/use-v-on-exact -->

<template>
    <div class="vrm-form__fieldset-item">
        <h4>{{ $t('dynamicess.priceCalculation') }}</h4>
        <div>
            <span v-html="$t('dynamicess.helperFormula')"></span>
        </div>
        <br />
        <div class="vrm-formula-builder__title" v-html="$t('dynamicess.enterFormula')" />
        <div
            class="vrm-formula-builder"
            :class="{
                'vrm-formula-builder--disabled': disabled,
            }"
        >
            <div ref="container" class="vrm-formula-builder__container">
                <div
                    v-for="(token, index) in computedTokens"
                    :key="'token' + index"
                    ref="tokens"
                    class="formula-token-with-separator"
                    :class="{ 'last-in-row': lastInRow[index] }"
                >
                    <div class="formula-token-separator">
                        <!-- keypress prevent in next div is needed for older browsers + mobile -->
                        <input
                            :id="'tokenSeparator' + index"
                            type="text"
                            :disabled="disabled"
                            @keydown.prevent="handleKey($event, index)"
                            @keyup.prevent="handleKey($event, index)"
                            @keypress.prevent
                            @focus="handleInputFocus(index)"
                            @blur="handleBlur"
                        />
                        <div
                            v-show="
                                suggestionsOpen === 2 * index &&
                                    dropdownOptions(
                                        computedTokens.slice(0, index).join('').length - 1,
                                    ).length > 0
                            "
                            class="formula-dropdown"
                        >
                            <ul class="formula-choices">
                                <li
                                    v-for="(suggestion, suggestionIndex) in dropdownOptions(
                                        computedTokens.slice(0, index).join('').length - 1,
                                    )"
                                    :key="suggestionIndex"
                                    class="formula-choice"
                                    @mousedown="handleSuggestion(suggestion, index)"
                                >
                                    <div
                                        class="formula-symbol"
                                        :class="
                                            suggestion === '*' ? 'sg-vrm-icon_055_cross-16px' : ''
                                        "
                                    >
                                        {{ suggestion === '*' ? '' : suggestion.toUpperCase() }}
                                    </div>
                                    <div v-if="suggestion === 'p'" class="formula-label">
                                        {{ NET_PRICES }}
                                    </div>
                                    <div v-else-if="suggestion === 'a'" class="formula-label">
                                        {{ $t('dynamicess.wizard.math.a') }}
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <!-- keypress prevent in next div is needed for older browsers + mobile -->
                    <div
                        :id="'formulaToken' + index"
                        class="formula-box"
                        :class="{
                            'formula-box--disabled': disabled,
                            'formula-box-number': isNumberToken(token),
                        }"
                        :contenteditable="!disabled && isNumberToken(token)"
                        @keydown="handleNumberEdit($event, index)"
                        @keyup="handleNumberEdit($event, index)"
                        @keypress.prevent
                        @blur="handleBlur"
                    >
                        <div v-if="token === '*'" class="sg-vrm-icon_055_cross-16px"></div>
                        <div v-else-if="token === 'a'">
                            <div class="formula-label">
                                abs(
                            </div>
                        </div>
                        <template v-else-if="token !== NET_PRICES">{{ token }}</template>
                        <div v-else class="net-price">
                            <div class="formula-symbol">
                                P
                            </div>
                            <div class="formula-label">
                                {{ NET_PRICES }}
                            </div>
                        </div>
                    </div>
                    <div v-if="lastInRow[index]" class="formula-token-separator last-in-row">
                        <!-- keypress prevent in next div is needed for older browsers + mobile -->
                        <input
                            :id="'lastInRowTokenSeparator' + index"
                            type="text"
                            :disabled="disabled"
                            @keydown.prevent="handleKey($event, index + 1)"
                            @keyup.prevent="handleKey($event, index + 1)"
                            @keypress.prevent
                            @focus="handleInputFocus(index, true)"
                            @blur="handleBlur"
                        />
                        <div
                            v-show="
                                suggestionsOpen === 2 * index + 1 &&
                                    dropdownOptions(
                                        computedTokens.slice(0, index + 1).join('').length - 1,
                                    ).length > 0
                            "
                            class="formula-dropdown"
                        >
                            <ul class="formula-choices">
                                <li
                                    v-for="(suggestion, suggestionIndex) in dropdownOptions(
                                        computedTokens.slice(0, index + 1).join('').length - 1,
                                    )"
                                    :key="suggestionIndex"
                                    class="formula-choice"
                                    @mousedown="handleSuggestion(suggestion, index + 1)"
                                >
                                    <div
                                        class="formula-symbol"
                                        :class="
                                            suggestion === '*' ? 'sg-vrm-icon_055_cross-16px' : ''
                                        "
                                    >
                                        {{ suggestion === '*' ? '' : suggestion.toUpperCase() }}
                                    </div>
                                    <div v-if="suggestion === 'p'" class="formula-label">
                                        {{ NET_PRICES }}
                                    </div>
                                    <div v-else-if="suggestion === 'a'" class="formula-label">
                                        {{ $t('dynamicess.wizard.math.a') }}
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div
                    class="formula-new-token"
                    :class="{ 'no-placeholder': !placeholder && computedTokens.length !== 0 }"
                >
                    <input
                        ref="newToken"
                        type="text"
                        :disabled="disabled"
                        :placeholder="
                            placeholder || computedTokens.length === 0
                                ? $t('dynamicess.wizard.formulaPlaceholder')
                                : ''
                        "
                        :class="{ 'no-placeholder': !placeholder && computedTokens.length !== 0 }"
                        @paste="handleFormulaPaste($event)"
                        @keydown.prevent.ctrl.v.exact="handleFormulaPaste($event)"
                        @keydown.prevent.meta.v.exact="handleFormulaPaste($event)"
                        @keydown.prevent="handleKey($event)"
                        @keyup.prevent="handleKey($event)"
                        @focus="handleInputFocus(computedTokens.length)"
                        @blur="handleBlur"
                    />
                    <div
                        v-show="
                            suggestionsOpen === computedTokens.length * 2 &&
                                dropdownOptions(value.length - 1).length > 0
                        "
                        :key="value"
                        class="formula-dropdown"
                        :class="{
                            shifted: newTokenDropdownShifted,
                        }"
                    >
                        <ul class="formula-choices">
                            <li
                                v-for="(suggestion, index) in dropdownOptions(value.length - 1)"
                                :key="index"
                                class="formula-choice"
                                @mousedown="handleSuggestion(suggestion)"
                            >
                                <div
                                    class="formula-symbol"
                                    :class="suggestion === '*' ? 'sg-vrm-icon_055_cross-16px' : ''"
                                >
                                    {{ suggestion === '*' ? '' : suggestion.toUpperCase() }}
                                </div>
                                <div v-if="suggestion === 'p'" class="formula-label">
                                    {{ NET_PRICES }}
                                </div>
                                <div v-else-if="suggestion === 'a'" class="formula-label">
                                    {{ $t('dynamicess.wizard.math.a') }}
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="formula-utilities">
                    <button class="sg-btn sg-site" @click="handleFormulaCopy">
                        {{ $t('common.copy', { i18nFallback: 'Copy' }) }}
                    </button>
                    <button v-if="!isFirefox" class="sg-btn sg-site" @click="handleFormulaPaste">
                        {{ $t('common.paste', { i18nFallback: 'Paste' }) }}
                    </button>
                </div>
            </div>
        </div>
        <div
            class="vrm-formula-builder__validation"
            :class="showErrorFormulaMessage() ? 'sg-color-red' : 'sg-color-green'"
        >
            <template v-if="showErrorFormulaMessage()">
                <div
                    class="vrm-formula-builder__validation-icon sg-vrm-icon_055_cross-16px sg-color-red"
                />
                {{ $t(`dynamicess.wizard.formulaInvalid`) + ': ' + errorMessage }}
            </template>
            <template v-else-if="showValidFormulaMessage()">
                <div
                    class="vrm-formula-builder__validation-icon sg-vrm-icon_054_check-16px sg-color-green"
                />
                {{ $t(`dynamicess.wizard.formulaValid`) }}
            </template>
        </div>
    </div>
</template>
<script>
import { Component, Model, Prop, Vue, Watch } from 'vue-property-decorator';
import { AngularDirective, ngInject } from '@/vue/decorators/angular-interop';
import VrmInfoTooltip from '@/vue/components/shared/VrmInfoTooltip';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import { evaluate, rationalize } from 'mathjs';
import VrmDropdown from '@/vue/components/shared/VrmDropdown';
import { absedFormula, removeImplicitMultiplication } from '../utils';

const TOKENS = {
    OPEN_P: '(',
    CLOSED_P: ')',
    ABS: 'a',
    NET_PRICE: 'p',
    NUMBERS: '1234567890',
    ADD: '+',
    SUB: '-',
    MUL: '*',
    DIV: '/',
    PERIOD: '.',
};

const PARENTHESIS_TOKENS = [TOKENS.OPEN_P, TOKENS.CLOSED_P].join('');
const OPERATIONS_TOKENS = [TOKENS.ADD, TOKENS.SUB, TOKENS.MUL, TOKENS.DIV].join('');
const OPEN_FORMULA_TOKENS = [TOKENS.NUMBERS + TOKENS.NET_PRICE + TOKENS.OPEN_P + TOKENS.ABS].join(
    '',
);

@Component({
    inheritAttrs: false,
    components: { VrmDropdown, VrmInfoTooltip },
})
@AngularLocaleBundle('form')
@AngularDirective('vrm-formula-builder')
export default class VrmFormulaBuilder extends Vue {
    @Prop()
    title;

    @Prop({ type: Boolean, default: false })
    required;

    @Model('input')
    model;

    @Prop({ type: String, default: '' })
    tooltip;

    @Prop({ type: Boolean, default: false })
    disabled;

    @Prop({
        type: Array,
        default: () => [],
        validator(list) {
            return list.every(({ check, message }) => {
                return _.isFunction(check) && _.isString(message);
            });
        },
    })
    checks;

    @ngInject()
    vrmNotificationsService;
    @ngInject()
    vrmNotificationType;

    NET_PRICES = this.$t('dynamicess.wizard.netPrice');
    valuePostedThisTick = false;
    errorMessage = null;
    tempValue = '';
    suggestionsOpen = false;
    placeholder = true;
    lastInRow = [];
    newTokenDropdownShifted = false;
    isFirefox = false;

    showErrorFormulaMessage() {
        return this.errorMessage && this.value !== '';
    }

    showValidFormulaMessage() {
        return !this.errorMessage && this.value !== '';
    }

    checkFormulaValidity(formula, defaultMessage) {
        if (formula === '') {
            this.errorMessage = null;
            return;
        }
        // check the value of the formula, in case of p = 0
        const scope = { p: 0 };
        let result = undefined;
        try {
            result = evaluate(absedFormula(formula), scope); // calculate the value
        } catch (e) {
            throw new TypeError(defaultMessage);
        }

        if (result === undefined) {
            throw new TypeError(defaultMessage);
        }

        if (result.toString() === 'Infinity' || isNaN(result)) {
            throw new TypeError(
                this.$t('dynamicess.mathExpressionInvalidMessage.dividedByZero', {
                    i18nFallback:
                        'The formula inserted has a divide by zero or there is a case where divide by zero is possible. Suggestion: check if you have a "1 / 0" or "1 / P".',
                }),
            );
        }

        // rationalizable expression in a rational fraction -> simplifies the expression to the most simple case or into to just one fraction
        let fraction = undefined;
        try {
            fraction = rationalize(formula.replaceAll(TOKENS.ABS, TOKENS.OPEN_P));
        } catch (e) {
            throw new TypeError(defaultMessage);
        }

        if (fraction === undefined) {
            throw new TypeError(defaultMessage);
        }

        if (!fraction.toString().includes(TOKENS.NET_PRICE)) {
            // expression should always have a p
            throw new TypeError(
                this.$t('dynamicess.mathExpressionInvalidMessage.includeP', {
                    i18nFallback:
                        'The formula should always include the P (net value) in the simplest form. Suggestion: you can have a "P - P" which is the same as not having a P.',
                }),
            );
        }
        if (
            fraction.fn === 'divide' &&
            fraction.args &&
            fraction.args.length > 1 &&
            fraction.args[1].toString().includes(TOKENS.NET_PRICE)
        ) {
            // if divide -> we know that there is a fraction and we can check if the denominator has 'p'
            throw new TypeError(
                this.$t('dynamicess.mathExpressionInvalidMessage.divideByP', {
                    i18nFallback:
                        'The formula in it\'s simplest, can\'t have the P (net value) as denominator. Suggestion: check if you are not dividing with a P, e.g. "2/(P-5)".',
                }),
            );
        }
    }

    get value() {
        const model = this.model || '';

        try {
            this.checkFormulaValidity(
                model,
                this.$t('dynamicess.mathExpressionInvalid', {
                    i18nFallback: 'Math expression is invalid',
                }),
            );
            this.errorMessage = null;
        } catch (e) {
            this.errorMessage = `${e.message}`;
        }
        return model.replaceAll(TOKENS.NET_PRICE, this.NET_PRICES);
    }

    set value(newValue) {
        this.tempValue = newValue;
        newValue = newValue.replaceAll(this.NET_PRICES, TOKENS.NET_PRICE);

        this.errorMessage = null;
        if (this.required && !newValue) {
            this.errorMessage = this.$t('form.required');
        }
        for (let i = 0; i < this.checks.length; i++) {
            const { check, error } = this.checks[i];
            if (!check(newValue)) {
                this.errorMessage = this.$t(error);
            }
        }
        try {
            this.checkFormulaValidity(
                newValue,
                this.$t('dynamicess.mathExpressionInvalid', {
                    i18nFallback: 'Math expression is invalid',
                }),
            );
        } catch (e) {
            this.errorMessage = `${e.message}`;
        }

        this.valuePostedThisTick = true;
        this.$emit('input', newValue);
        this.$nextTick(() => {
            this.valuePostedThisTick = false;
        });
    }

    created() {
        this.value = this.makeMultiplicationExplicit(this.value);
        window.addEventListener('resize', this.setLastInRow);
        //firefox disable paste as clipboard.readtext is not supported
        this.isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    }

    beforeDestroy() {
        window.removeEventListener('resize', this.setLastInRow);
    }

    @Watch('value')
    @Watch('placeholder')
    setLastInRow() {
        if (this.value === '') return;
        this.$nextTick(() => {
            if (!this.$refs.tokens || !this.$refs.newToken) return;
            const tokens = this.$refs.tokens;
            const newToken = this.$refs.newToken;
            for (let i = 0; i < tokens.length - 1; i++) {
                this.lastInRow[i] =
                    tokens[i].getBoundingClientRect().x > tokens[i + 1].getBoundingClientRect().x;
            }
            this.lastInRow[tokens.length - 1] =
                tokens[tokens.length - 1].getBoundingClientRect().x >
                newToken.getBoundingClientRect().x;
            this.newTokenDropdownShifted = newToken.getBoundingClientRect().width < 100;
            this.$forceUpdate();
        });
    }

    handleBlur() {
        this.suggestionsOpen = false;
    }

    @Watch('errorMessage')
    onError() {
        this.$emit('error', !!this.errorMessage);
    }

    get computedTokens() {
        return this.getTokens(this.value);
    }

    dropdownOptions(selectionStart) {
        let previousCharPosition = selectionStart;
        while (this.value.charAt(previousCharPosition) === ' ' && previousCharPosition > 0) {
            previousCharPosition--;
        }
        return this.possibleNextCharacters(previousCharPosition, this.value)
            .replaceAll(TOKENS.NUMBERS, '')
            .replaceAll(TOKENS.PERIOD, '')
            .split('');
    }

    handleSuggestion(token, index = null) {
        if (token === TOKENS.NET_PRICE) {
            token = this.NET_PRICES;
        }

        if (index === null) {
            this.value += token;
            setTimeout(() => {
                let el = this.$refs.newToken;
                el.focus();
            }, 0);
        } else {
            let tokens = this.getTokens(this.value);
            this.value =
                (index !== 0 ? tokens.slice(0, index).join('') : '') +
                token +
                tokens.slice(index).join('');

            setTimeout(() => {
                this.focusOnSeparator(index + 1);
            }, 0);
        }
    }

    handleInputFocus(index, isAfterToken = false) {
        this.placeholder = false;
        /*
        In the last token of the row there is an additional separator after the token
        In that case the dropdown should be shown there, but will have suggestions for after the token
        For this distinction each token has a before suggestion and after suggestion (hence the "2 *")
         */
        this.suggestionsOpen = 2 * index + (isAfterToken ? 1 : 0);
    }

    possibleNextCharacters(lastNonSpacePosition, fullString) {
        let char = lastNonSpacePosition >= 0 ? fullString.charAt(lastNonSpacePosition) : '';
        let possibleNext = '';
        if (char === '') {
            possibleNext += OPEN_FORMULA_TOKENS;
        } else if ((TOKENS.NUMBERS + TOKENS.NET_PRICE).includes(char)) {
            possibleNext += TOKENS.NUMBERS + TOKENS.NET_PRICE + OPERATIONS_TOKENS;
            if (this.numOpenParentheses(fullString) > 0) {
                possibleNext += TOKENS.CLOSED_P;
            }
            let canAddPeriod = true;
            while (
                (TOKENS.NUMBERS + ' ').includes(fullString.charAt(lastNonSpacePosition)) &&
                lastNonSpacePosition > 0
            ) {
                lastNonSpacePosition--;
                if (TOKENS.PERIOD.includes(fullString.charAt(lastNonSpacePosition)))
                    canAddPeriod = false;
            }
            if (canAddPeriod) possibleNext += TOKENS.PERIOD;
        } else if (OPERATIONS_TOKENS.includes(char)) {
            possibleNext += OPEN_FORMULA_TOKENS;
        } else if (char === TOKENS.OPEN_P || char === TOKENS.ABS) {
            possibleNext += TOKENS.NUMBERS + TOKENS.NET_PRICE + TOKENS.SUB + TOKENS.OPEN_P;
        } else if (char === TOKENS.CLOSED_P || char === this.NET_PRICES.slice(-1)) {
            possibleNext += OPERATIONS_TOKENS;
            if (this.numOpenParentheses(fullString) > 0) {
                possibleNext += TOKENS.CLOSED_P;
            }
        } else if (TOKENS.PERIOD.includes(char)) {
            possibleNext += TOKENS.NUMBERS + TOKENS.NET_PRICE;
        }
        return possibleNext;
    }

    numOpenParentheses(fullString) {
        let openParentheses = 0;
        for (const char of fullString) {
            openParentheses +=
                char === TOKENS.OPEN_P || char === TOKENS.ABS
                    ? 1
                    : char === TOKENS.CLOSED_P
                    ? -1
                    : 0;
            if (openParentheses < 0) {
                return false; // Can't use closing parenthesis when there is no open parenthesis
            }
        }
        return openParentheses;
    }

    makeMultiplicationExplicit(formulaString) {
        formulaString = formulaString.replaceAll(this.NET_PRICES, TOKENS.NET_PRICE);
        formulaString = removeImplicitMultiplication(formulaString);
        return formulaString.replaceAll(TOKENS.NET_PRICE, this.NET_PRICES);
    }

    handleFormulaPaste(event) {
        event.preventDefault();
        navigator.clipboard.readText().then(text => {
            let temp = ''.concat(this.value, this.getTokens(text).join(''));
            temp = this.makeMultiplicationExplicit(temp);
            this.value = temp;
            this.suggestionsOpen = false;
            document.activeElement.blur();
        });
    }
    handleFormulaCopy(event) {
        event.preventDefault();
        navigator.clipboard.writeText(this.value).then(() => {
            this.vrmNotificationsService.add({
                type: this.vrmNotificationType.SUCCESS,
                title: this.$t('diagnostics.clipboard_copy_success', {
                    i18nFallback: 'Content copied to clipboard',
                }),
            });
        });
    }

    getTokens(string) {
        let tokens = [];
        let currentToken = '';

        string = string.replaceAll(this.NET_PRICES, TOKENS.NET_PRICE);
        for (const char of string) {
            if (char === ' ') {
                continue;
            }
            if (char === TOKENS.NET_PRICE) {
                if (currentToken !== '') {
                    tokens.push(currentToken);
                    currentToken = '';
                }
                tokens.push(this.NET_PRICES);
            }

            if ((TOKENS.ABS + PARENTHESIS_TOKENS + OPERATIONS_TOKENS).includes(char)) {
                if (currentToken !== '') {
                    tokens.push(currentToken);
                    currentToken = '';
                }
                tokens.push(char);
            } else if ((TOKENS.NUMBERS + TOKENS.PERIOD).includes(char)) {
                currentToken += char;
            }
        }
        if (currentToken !== '') {
            tokens.push(currentToken);
        }
        return tokens;
    }

    handleKey($event, index = null) {
        let tokens = this.getTokens(this.value);
        let fullString = index === null ? this.value : tokens.slice(0, index).join('');
        let previousCharPosition = fullString.length - 1;
        while (fullString.charAt(previousCharPosition) === ' ' && previousCharPosition > 0) {
            previousCharPosition--;
        }
        let allowedCharacters = this.possibleNextCharacters(previousCharPosition, fullString);
        let key = $event.key ? $event.key : String.fromCharCode($event.which);
        key = key === 'P' ? 'p' : key;
        if (
            $event.which === 229 &&
            $event.target &&
            $event.target.value &&
            $event.target.value.length > 0
        ) {
            if ($event.type === 'keyup') {
                key = $event.target.value.charAt($event.target.value.length - 1);
                $event.target.value = '';
            } else {
                $event.preventDefault();
                return;
            }
        } else if ($event.type === 'keyup') {
            $event.preventDefault();
            return;
        }

        index = index === null ? tokens.length : index;
        if (['ArrowLeft', 'ArrowRight', 'Tab'].includes(key)) {
            if (key === 'ArrowLeft') {
                this.focusOnSeparator(index - 1);
            } else {
                this.focusOnSeparator(index + 1);
            }
        } else if (key === 'Backspace' && index > 0) {
            if (index < tokens.length) {
                tokens.splice(index - 1, 1);
                let newValue = tokens.join('');
                this.value = tokens.join('');
                if (this.getTokens(newValue).length === tokens.length - 1) {
                    this.focusOnSeparator(index - 2);
                } else {
                    this.focusOnSeparator(index - 1);
                }
            } else {
                let newValue =
                    tokens.slice(-1)[0] !== this.NET_PRICES
                        ? this.value.slice(0, -1)
                        : tokens.slice(0, -1).join('');
                this.suggestionsOpen = this.getTokens(newValue).length * 2;
                this.value = newValue;
            }
        } else if (!$event.repeat && allowedCharacters.includes(key)) {
            let newValue =
                index < tokens.length
                    ? tokens.slice(0, index).join('') + key + tokens.slice(index).join('')
                    : this.value + key;
            this.value = newValue;
            if (index < tokens.length) {
                let newTokens = this.getTokens(newValue);
                if (newTokens.length > tokens.length) {
                    setTimeout(() => {
                        this.focusOnSeparator(index + 1);
                    }, 0);
                } else {
                    this.focusOnSeparator(index);
                }
            } else {
                this.suggestionsOpen = this.getTokens(newValue).length * 2;
            }
        }
    }

    handleNumberEdit($event, index) {
        let selection = document.getSelection();
        let selectionStart;
        if (selection.rangeCount) {
            let range = selection.getRangeAt(0);
            let range2 = range.cloneRange();
            range2.selectNodeContents($event.target); // select content
            selectionStart = range.startOffset;
        }
        let tokens = this.getTokens(this.value);
        let key = $event.key ? $event.key : String.fromCharCode($event.which);

        const targetValue = $event.target.innerHTML;
        if ($event.which === 229 && $event.target && targetValue && targetValue.length > 0) {
            if ($event.type === 'keyup') {
                key = targetValue.charAt(selectionStart - 1);
                selectionStart--;
            } else {
                $event.preventDefault();
                return;
            }
        } else if ($event.type === 'keyup') {
            $event.preventDefault();
            return;
        }

        let fullString = tokens.slice(0, index).join('') + $event.target.innerHTML;

        let previousCharPosition = tokens.slice(0, index).join('').length + selectionStart - 1;
        while (fullString.charAt(previousCharPosition) === ' ' && previousCharPosition > 0) {
            previousCharPosition--;
        }
        let allowedCharacters = this.possibleNextCharacters(previousCharPosition, fullString);
        key = key === 'P' ? 'p' : key;
        if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab'].includes(key)) {
            return true;
        } else if (key === 'Backspace') {
            $event.preventDefault();
            if (selectionStart !== 0) {
                this.value =
                    tokens.slice(0, index).join('') +
                    $event.target.innerHTML.substring(0, selectionStart - 1) +
                    $event.target.innerHTML.substring(selectionStart) +
                    tokens.slice(index + 1).join('');
            }
            this.focusOnToken(index, selectionStart > 0 ? selectionStart - 1 : 0);
        } else if (
            !$event.repeat &&
            allowedCharacters.includes(key) &&
            (TOKENS.NUMBERS + TOKENS.PERIOD).includes(key)
        ) {
            $event.preventDefault();
            if (key === TOKENS.PERIOD && $event.target.innerHTML.includes(TOKENS.PERIOD)) {
                if (
                    $event.type === 'keyup' &&
                    (
                        $event.target.innerHTML.substring(0, selectionStart) +
                        $event.target.innerHTML.substring(selectionStart + 1)
                    ).includes(TOKENS.PERIOD)
                ) {
                    $event.target.innerHTML =
                        targetValue.substring(0, selectionStart) +
                        targetValue.substring(selectionStart + 1);
                    this.value =
                        tokens.slice(0, index).join('') +
                        $event.target.innerHTML +
                        tokens.slice(index + 1).join('');
                    this.focusOnToken(index, selectionStart);
                }
                return;
            }

            this.value =
                $event.type === 'keyup'
                    ? tokens.slice(0, index).join('') +
                      $event.target.innerHTML +
                      tokens.slice(index + 1).join('')
                    : tokens.slice(0, index).join('') +
                      $event.target.innerHTML.substring(0, selectionStart) +
                      key +
                      $event.target.innerHTML.substring(selectionStart) +
                      tokens.slice(index + 1).join('');
            // this.$forceUpdate();
            this.focusOnToken(index, selectionStart + 1);
        } else {
            if ($event.type === 'keyup') {
                $event.target.innerHTML =
                    targetValue.substring(0, selectionStart) +
                    targetValue.substring(selectionStart + 1);
                this.value =
                    tokens.slice(0, index).join('') +
                    $event.target.innerHTML +
                    tokens.slice(index + 1).join('');
                this.focusOnToken(index, selectionStart);
            }
            $event.preventDefault();
        }
    }

    focusOnSeparator(index) {
        let el = document.getElementById('tokenSeparator' + index);
        if (el) {
            el.focus();
        } else if (index >= this.getTokens(this.value).length) {
            this.$refs.newToken.focus();
        }
    }

    focusOnToken(tokenIndex, charIndex = 0) {
        this.$nextTick(() => {
            let el = document.getElementById('formulaToken' + tokenIndex);
            let selection = document.getSelection();
            if (selection.rangeCount) {
                let range = selection.getRangeAt(0);
                range.setStart(
                    el.childNodes[0],
                    charIndex < 0 ? el.childNodes[0].length + 1 + charIndex : charIndex,
                );
                range.collapse(true);
                selection.removeAllRanges();
                selection.addRange(range);
                el.focus();
            }
        });
    }

    isNumberToken(token) {
        return (
            !/\D/.test(token) ||
            (token.split(TOKENS.PERIOD).length === 2 &&
                !/\D/.test(token.replaceAll(TOKENS.PERIOD, '')))
        );
    }
}
</script>
